import { useEffect, useRef, useState } from 'react'
import './index.scss'
import { Button, Progress, Tag, Divider } from 'antd'
import { CaretRightOutlined } from '@ant-design/icons'

const CEREMONY_MS = 2800

type ThirdStepProps = {
    data: {
        display_name: string
        start: string
        end: string
        freq: string
    }
    runId: string
    onGoToList: () => void
}

export default function ThirdStep({ data, runId, onGoToList }: ThirdStepProps) {
    const headerTitle = data.display_name || '量化回测任务'
    const rangeText = [data.start, data.end].filter(Boolean).join(' ~ ')
    const summaryText = [headerTitle, rangeText, data.freq].filter(Boolean).join('  ')
    const [progress, setProgress] = useState(8)
    const [remainSec, setRemainSec] = useState(3)
    const navigatedRef = useRef(false)

    const logs = [
        `回测记录已创建（run_id=${runId}）`,
        '任务已进入后台执行队列',
        '可返回列表查看「进行中」状态，完成后进入详情',
    ]

    const goListOnce = () => {
        if (navigatedRef.current) return
        navigatedRef.current = true
        onGoToList()
    }

    useEffect(() => {
        const startedAt = Date.now()
        const tick = window.setInterval(() => {
            const elapsed = Date.now() - startedAt
            const ratio = Math.min(1, elapsed / CEREMONY_MS)
            setProgress(Math.round(8 + ratio * 92))
            setRemainSec(Math.max(0, Math.ceil((CEREMONY_MS - elapsed) / 1000)))
            if (elapsed >= CEREMONY_MS) {
                window.clearInterval(tick)
                goListOnce()
            }
        }, 80)
        return () => window.clearInterval(tick)
        // 仅挂载时启动倒计时；通过 goListOnce 保证只跳转一次
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <section className="third-step">
            <div className="third-step__meta">
                <div className="third-step__meta-title font-weight-700">
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    <span style={{ marginLeft: 6 }}>{headerTitle}</span>
                </div>
                <div className="third-step__meta-range">{rangeText}</div>
            </div>

            <div className="third-step__panel">
                <div className="third-step__title font-weight-700">回测已启动</div>
                <div className="third-step__subtitle">
                    {summaryText}
                    <div style={{ marginTop: 4 }}>三智能体已接手，后台执行中，可离开本页。</div>
                </div>

                <div className="third-step__progress">
                    <Progress percent={progress} status="active" showInfo={false} />
                </div>

                <div className="third-step__timeline">
                    <Tag variant='filled' className='is-purple text-10'>研究</Tag>
                    研究Agent — 扫描universe，因子评分
                    <Tag variant='filled' className='is-success text-10'>决策</Tag>
                    决策Agent — 生成调仓方案
                    <Tag variant='filled' className='is-warning text-10'>风控</Tag>
                    风控Agent — 审核约束合规
                </div>

                <div className="third-step__log">
                    {logs.map((item, index) => (
                        <div key={item} className="third-step__log-line">
                            {index === 0 ? '[系统] ' : `【${index}】 `}
                            {item}
                        </div>
                    ))}
                    <Divider className='third-step__log-divider text-10'>
                        <CaretRightOutlined /> {remainSec > 0 ? `${remainSec}s 后前往列表` : '正在前往列表…'}
                    </Divider>
                </div>

                <div className="third-step__actions">
                    <Button type="primary" className="text-12" onClick={goListOnce}>
                        前往回测列表
                    </Button>
                </div>
            </div>
        </section>
    )
}
